import { useState } from 'react';
import { formatMoney, parseAmount, pickForLanguage } from '@domain';
import type { ReferenceData } from '@domain';
import type { MoneyEntry } from '../api.js';
import { bankAccounts } from '../data/accounts.js';
import type { Lang, Translate } from '../i18n/index.js';
import { SearchableSelect, type SelectOption } from '../components/SearchableSelect.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';

/**
 * Moving money between two of the company's own accounts.
 *
 * Not a payment to anybody and not an expense: the company is no poorer after
 * it, which is why it must not be entered as money out. Xero writes both
 * halves itself from one object, so the two sides cannot drift apart — see
 * api/src/domain/money.ts.
 *
 * The commonest real use here is the one every Qatari SME has: cash drawn
 * from the current account into petty cash, every week, forever.
 */

export function TransferForm({
  reference,
  currency,
  busy,
  lang,
  t,
  onRecord,
}: {
  reference: ReferenceData;
  currency: string;
  busy: boolean;
  lang: Lang;
  t: Translate;
  onRecord: (entry: MoneyEntry) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [fromAccountCode, setFrom] = useState('');
  const [toAccountCode, setTo] = useState('');
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');

  const banks = bankAccounts(reference.accounts);
  const options: SelectOption[] = banks.map((account) => ({
    value: account.code,
    label: `${account.code} · ${pickForLanguage(account.name, account.description, lang)}`,
  }));

  const value = parseAmount(amount);

  /*
    Said before the button is pressed, not after Xero refuses it.

    Both dropdowns landing on the same account is a half-finished form — one
    changed, the other forgotten — and it is the sort of thing somebody only
    notices when the ledger is already wrong.
  */
  const sameAccount =
    fromAccountCode !== '' && toAccountCode !== '' && fromAccountCode === toAccountCode;

  const onlyOneAccount = banks.length < 2;
  const ready =
    fromAccountCode !== '' && toAccountCode !== '' && !sameAccount && value > 0 && !busy;

  const field = {
    height: '36px',
    width: '100%',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
  };

  const label = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' };

  return (
    <Card>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${theme.dividerSoft}`,
          fontWeight: 600,
          fontSize: '13px',
          background: theme.surfaceHeader,
        }}
      >
        {t('Transfer')}
      </div>

      <div style={{ padding: '16px' }}>
        {onlyOneAccount && (
          <div
            style={{
              background: theme.surfaceMuted,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radiusControl,
              padding: '10px 12px',
              marginBottom: '14px',
              fontSize: '12.5px',
              color: theme.textMuted,
            }}
          >
            {t('A transfer needs two bank accounts, and this company has one.')}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
          <label style={{ flex: '1 1 240px', minWidth: 0 }}>
            <span style={label}>{t('Out of')}</span>
            <SearchableSelect
              value={fromAccountCode}
              options={options}
              placeholder={t('Choose…')}
              emptyLabel={t('No bank account in Xero for this company')}
              invalid={sameAccount}
              onChange={setFrom}
              t={t}
            />
          </label>

          <label style={{ flex: '1 1 240px', minWidth: 0 }}>
            <span style={label}>{t('Into')}</span>
            <SearchableSelect
              value={toAccountCode}
              options={options}
              placeholder={t('Choose…')}
              emptyLabel={t('No bank account in Xero for this company')}
              invalid={sameAccount}
              onChange={setTo}
              t={t}
            />
          </label>
        </div>

        {sameAccount && (
          <div
            style={{ fontSize: '12.5px', color: theme.badDeep, fontWeight: 600, marginBottom: '12px' }}
          >
            {t('A transfer needs two different accounts.')}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
          <label style={{ flex: '0 1 170px', minWidth: 0 }}>
            <span style={label}>{t('Date')}</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={field} />
          </label>

          <label style={{ flex: '0 1 190px', minWidth: 0 }}>
            <span style={label}>{t('Amount')}</span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              dir="ltr"
              style={{ ...field, fontFamily: theme.fontMono }}
            />
          </label>

          <label style={{ flex: '1 1 240px', minWidth: 0 }}>
            <span style={label}>
              {t('Reference')}{' '}
              <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
            </span>
            <input
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder={t('Bank transfer number, cheque number')}
              style={field}
            />
          </label>
        </div>

        {value > 0 && (
          <div style={{ fontSize: '12.5px', color: theme.textMuted, marginBottom: '16px' }}>
            <span dir="ltr" style={{ fontFamily: theme.fontMono, color: theme.text, fontWeight: 600 }}>
              {formatMoney(value)} {currency}
            </span>{' '}
            {t('leaves one of your accounts and arrives in the other. Nothing is spent.')}
          </div>
        )}

        <button
          type="button"
          disabled={!ready}
          onClick={() =>
            onRecord({
              kind: 'transfer',
              fromAccountCode,
              toAccountCode,
              date,
              amount,
              reference: memo,
            })
          }
          style={{
            height: '42px',
            minWidth: '220px',
            background: ready ? theme.nav : theme.borderStrong,
            color: '#fff',
            border: 0,
            borderRadius: theme.radiusControl,
            fontSize: '14px',
            fontWeight: 600,
            cursor: ready ? 'pointer' : 'default',
          }}
        >
          {busy ? t('Recording…') : t('Record the transfer')}
        </button>

        <div style={{ marginTop: '9px', fontSize: '11.5px', color: theme.textMuted, lineHeight: 1.6 }}>
          {t('Both sides are written in Xero at once. A transfer has no draft to approve.')}
        </div>
      </div>
    </Card>
  );
}
